/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from 'react';

// ==================== CONSTANTS ====================
const MATERIAL_CONSTANTS = {
  STEEL_DENSITY: 7.85,
  LENGTH_CONVERSION_FACTOR: 1000,
  TUBE_CALCULATION_FACTOR: 0.024662,
  COIL_WEIGHTS: {
    GI: 2879.95,
    HR: 2356.59,
  },
} as const;

// ==================== TYPES ====================
type SteelType = keyof typeof MATERIAL_CONSTANTS.COIL_WEIGHTS;
type TubeType = 'Round' | 'Square' | 'Rectangle';

interface BaseFormValues {
  // Board calculation fields
  face_width?: string | number;
  bending?: string | number;
  height?: string | number;
  total_width?: string | number;
  boards_thickness?: string | number;
  boards_length?: string | number;
  
  // Steel calculation fields
  steel_length?: string | number;
  steel_width?: string | number;
  steel_thickness?: string | number;
  steel_type?: SteelType;
  
  // Tube calculation fields
  tube_type?: TubeType;
  tube_od?: string | number;
  shape_width?: string | number;
  shape_height?: string | number;
  material_thickness?: string | number;
  material_length?: string | number;
}

interface MaterialCalculations {
  totalWidth: number;
  materialUnit: number;
  boardsWeight: number;
  steelWeight: number;
  materialWeight: number;
}

interface CalculationResult<T = number> {
  value: T;
  isValid: boolean;
  error?: string;
}

// ==================== UTILITY FUNCTIONS ====================
/**
 * Safely parses a value to number with comprehensive validation
 */
const safeParseNumber = (value: string | number | undefined | null): CalculationResult<number> => {
  if (value === undefined || value === null || value === '') {
    return { value: 0, isValid: false, error: 'Empty value' };
  }
  
  const parsed = typeof value === 'string' ? parseFloat(value.trim()) : Number(value);
  
  if (isNaN(parsed)) {
    return { value: 0, isValid: false, error: 'Invalid number format' };
  }
  
  if (!isFinite(parsed)) {
    return { value: 0, isValid: false, error: 'Number is not finite' };
  }
  
  return { value: parsed, isValid: true };
};

/**
 * Validates that all provided values are positive numbers
 */
const validatePositiveNumbers = (...values: (string | number | undefined)[]): boolean => {
  return values.every(value => {
    const result = safeParseNumber(value);
    return result.isValid && result.value > 0;
  });
};

/**
 * Creates a memoization key from form values to optimize re-calculations
 */
const createMemoKey = (formValues: BaseFormValues | undefined): string => {
  if (!formValues) return 'empty';
  
  // Only include relevant fields to avoid unnecessary recalculations
  const relevantFields = [
    'face_width', 'bending', 'height', 'total_width', 'boards_thickness', 'boards_length',
    'steel_length', 'steel_width', 'steel_thickness', 'steel_type',
    'tube_type', 'tube_od', 'shape_width', 'shape_height', 'material_thickness', 'material_length'
  ];
  
  return relevantFields
    .map(field => `${field}:${formValues[field as keyof BaseFormValues] ?? ''}`)
    .join('|');
};

// ==================== CALCULATION FUNCTIONS ====================
/**
 * Calculates the total width for boards costing
 * Formula: ((height + bending) * 2) + face_width
 */
const calculateBoardsTotalWidth = (formValues: BaseFormValues): CalculationResult<number> => {
  const { face_width, bending, height } = formValues;
  
  if (!validatePositiveNumbers(face_width, bending, height)) {
    return { value: 0, isValid: false, error: 'Invalid input values for boards total width calculation' };
  }
  
  const faceWidthResult = safeParseNumber(face_width);
  const bendingResult = safeParseNumber(bending);
  const heightResult = safeParseNumber(height);
  
  if (![faceWidthResult, bendingResult, heightResult].every(r => r.isValid)) {
    return { value: 0, isValid: false, error: 'Failed to parse input values' };
  }
  
  const totalWidth = ((heightResult.value + bendingResult.value) * 2) + faceWidthResult.value;
  
  return { value: totalWidth, isValid: true };
};

/**
 * Calculates the weight of boards
 * Formula: (total_width * thickness * (length / 1000)) * 7.85
 */
const calculateBoardsWeight = (formValues: BaseFormValues): CalculationResult<number> => {
  const { total_width, boards_thickness, boards_length } = formValues;
  
  if (!validatePositiveNumbers(total_width, boards_thickness, boards_length)) {
    return { value: 0, isValid: false, error: 'Invalid input values for boards weight calculation' };
  }
  
  const totalWidthResult = safeParseNumber(total_width);
  const thicknessResult = safeParseNumber(boards_thickness);
  const lengthResult = safeParseNumber(boards_length);
  
  if (![totalWidthResult, thicknessResult, lengthResult].every(r => r.isValid)) {
    return { value: 0, isValid: false, error: 'Failed to parse input values' };
  }
  
  const weight = (totalWidthResult.value * thicknessResult.value * 
    (lengthResult.value / MATERIAL_CONSTANTS.LENGTH_CONVERSION_FACTOR)) * 
    MATERIAL_CONSTANTS.STEEL_DENSITY;
  
  return { value: weight, isValid: true };
};

/**
 * Calculates the weight of steel
 * Formula: (length * width * thickness * 7.85) / 1000
 */
const calculateSteelWeight = (formValues: BaseFormValues): CalculationResult<number> => {
  const { steel_length, steel_width, steel_thickness } = formValues;
  
  if (!validatePositiveNumbers(steel_length, steel_width, steel_thickness)) {
    return { value: 0, isValid: false, error: 'Invalid input values for steel weight calculation' };
  }
  
  const lengthResult = safeParseNumber(steel_length);
  const widthResult = safeParseNumber(steel_width);
  const thicknessResult = safeParseNumber(steel_thickness);
  
  if (![lengthResult, widthResult, thicknessResult].every(r => r.isValid)) {
    return { value: 0, isValid: false, error: 'Failed to parse input values' };
  }
  
  const weight = (lengthResult.value * widthResult.value * thicknessResult.value * 
    MATERIAL_CONSTANTS.STEEL_DENSITY) / MATERIAL_CONSTANTS.LENGTH_CONVERSION_FACTOR;
  
  return { value: weight, isValid: true };
};

/**
 * Gets the material unit weight based on steel type
 */
const calculateMaterialUnit = (formValues: BaseFormValues): CalculationResult<number> => {
  const { steel_type } = formValues;
  
  if (!steel_type) {
    return { value: 0, isValid: false, error: 'Steel type not specified' };
  }
  
  if (!(steel_type in MATERIAL_CONSTANTS.COIL_WEIGHTS)) {
    return { value: 0, isValid: false, error: `Unsupported steel type: ${steel_type}` };
  }
  
  const weight = MATERIAL_CONSTANTS.COIL_WEIGHTS[steel_type];
  return { value: weight, isValid: true };
};

/**
 * Calculates material weight based on tube type and dimensions
 * Supports Round, Square, and Rectangle tube types
 */
const calculateTubeMaterialWeight = (formValues: BaseFormValues): CalculationResult<number> => {
  const { tube_type, tube_od, shape_width, shape_height, material_thickness, material_length } = formValues;
  
  if (!tube_type) {
    return { value: 0, isValid: false, error: 'Tube type not specified' };
  }
  
  const thicknessResult = safeParseNumber(material_thickness);
  const lengthResult = safeParseNumber(material_length);
  
  if (!thicknessResult.isValid || !lengthResult.isValid) {
    return { value: 0, isValid: false, error: 'Invalid thickness or length values' };
  }
  
  let weight = 0;
  
  switch (tube_type) {
    case 'Round': {
      const odResult = safeParseNumber(tube_od);
      if (!odResult.isValid || odResult.value <= thicknessResult.value) {
        return { value: 0, isValid: false, error: 'Invalid outer diameter for round tube' };
      }
      
      weight = (odResult.value - thicknessResult.value) * thicknessResult.value * 
        MATERIAL_CONSTANTS.TUBE_CALCULATION_FACTOR * lengthResult.value;
      break;
    }
    
    case 'Square':
    case 'Rectangle': {
      const widthResult = safeParseNumber(shape_width);
      const heightResult = safeParseNumber(shape_height);
      
      if (!widthResult.isValid || !heightResult.isValid) {
        return { value: 0, isValid: false, error: 'Invalid width or height for rectangular tube' };
      }
      
      const perimeter = 2 * (widthResult.value + heightResult.value);
      weight = (((perimeter / 3.14) - thicknessResult.value) * thicknessResult.value * 
        MATERIAL_CONSTANTS.TUBE_CALCULATION_FACTOR) * lengthResult.value;
      break;
    }
    
    default:
      return { value: 0, isValid: false, error: `Unsupported tube type: ${tube_type}` };
  }
  
  return { value: Math.max(0, weight), isValid: true };
};

// ==================== MAIN HOOK ====================
/**
 * Custom hook for optimized material calculations with comprehensive error handling
 * and performance optimization through memoization
 */
export const useMaterialCalculations = (formValues?: BaseFormValues): MaterialCalculations => {
  const calculations = useMemo(() => {
    if (!formValues) {
      return {
        totalWidth: 0,
        materialUnit: 0,
        boardsWeight: 0,
        steelWeight: 0,
        materialWeight: 0,
      };
    }
    
    // Calculate all values with error handling
    const totalWidthResult = calculateBoardsTotalWidth(formValues);
    const materialUnitResult = calculateMaterialUnit(formValues);
    const boardsWeightResult = calculateBoardsWeight(formValues);
    const steelWeightResult = calculateSteelWeight(formValues);
    const materialWeightResult = calculateTubeMaterialWeight(formValues);
    
    // Optional: Log errors in development
    if (process.env.NODE_ENV === 'development') {
      [totalWidthResult, materialUnitResult, boardsWeightResult, steelWeightResult, materialWeightResult]
        .filter(result => !result.isValid && result.error)
        .forEach(result => console.warn('Calculation warning:', result.error));
    }
    
    return {
      totalWidth: totalWidthResult.value,
      materialUnit: materialUnitResult.value,
      boardsWeight: boardsWeightResult.value,
      steelWeight: steelWeightResult.value,
      materialWeight: materialWeightResult.value,
    };
  }, [createMemoKey(formValues)]); // Optimized dependency to prevent unnecessary recalculations
  
  return calculations;
};

// ==================== ADDITIONAL UTILITIES ====================
/**
 * Utility function to validate form values before calculations
 */
export const validateFormValues = (formValues: BaseFormValues): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const { tube_type, steel_type } = formValues;
  
  // Validate steel type if provided
  if (steel_type && !(steel_type in MATERIAL_CONSTANTS.COIL_WEIGHTS)) {
    errors.push(`Invalid steel type: ${steel_type}`);
  }
  
  // Validate tube type if provided
  if (tube_type && !['Round', 'Square', 'Rectangle'].includes(tube_type)) {
    errors.push(`Invalid tube type: ${tube_type}`);
  }
  
  return { isValid: errors.length === 0, errors };
};

/**
 * Get available steel types for form validation
 */
export const getAvailableSteelTypes = (): SteelType[] => {
  return Object.keys(MATERIAL_CONSTANTS.COIL_WEIGHTS) as SteelType[];
};

/**
 * Get available tube types for form validation
 */
export const getAvailableTubeTypes = (): TubeType[] => {
  return ['Round', 'Square', 'Rectangle'];
};

export default {
  useMaterialCalculations,
  validateFormValues,
  getAvailableSteelTypes,
  getAvailableTubeTypes,
  MATERIAL_CONSTANTS,
};
export type {
  BaseFormValues,
  MaterialCalculations,
  SteelType,
  TubeType,
  CalculationResult,
};